import { useQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { getPropertyChartDataRealtime, RealtimeChartDataResponse, RealtimeFilterValues } from '../api/fetchRealtimeProperty';
import { DeviceType } from '@/features/charts/api/fetchChartData';

export interface RealtimeFilterOptions {
  days: number[];
  devices: readonly ["mobile", "desktop"];
  los: number[];
  adults: number[];
  rooms: number[];
}

// Default filter options since the backend no longer provides them
export const DEFAULT_RUNTIME_FILTER_OPTIONS: RealtimeFilterOptions = {
  days: [30, 60, 90, 120, 180, 365],
  devices: ["mobile", "desktop"] as const,
  los: [1, 2, 3, 4, 5, 6, 7, 14, 30],
  adults: [1, 2, 3, 4, 5, 6],
  rooms: [1, 2, 3, 4, 5, 6]
};

interface UseRealtimePropertyOptions {
  useDemoData?: boolean;
  demoData?: RealtimeChartDataResponse;
}

const defaultValues: RealtimeFilterValues = {
  length_of_stay: 2,
  adults: 2,
  rooms: 1,
  days: 30,
  device_type: 'mobile',
}

export const useRealtimePropertyWithFilters = (
  propertyId: number | null,
  options?: UseRealtimePropertyOptions
) => {
  // Local state for filters
  const [filters, setFilters] = useState<RealtimeFilterValues | undefined>();

  // API call with optional demo data
  const propertyChartResponse = useQuery({
    queryKey: ['property-rankings', propertyId, filters, options?.useDemoData],
    queryFn: async () => {
      if (options?.useDemoData && options.demoData) {
        return options.demoData;
      }
      return getPropertyChartDataRealtime(propertyId!, filters);
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
    refetchOnWindowFocus: false,
  });

  // Extract current filter values from API response
  const serverFilters = useMemo(() => {
    if (!propertyChartResponse.data?.filters) {
      return null;
    }

    // Extract current values from the filters object
    const currentValues: RealtimeFilterValues = {
      length_of_stay: propertyChartResponse.data.filters.length_of_stay,
      adults: propertyChartResponse.data.filters.adults,
      rooms: propertyChartResponse.data.filters.rooms,
      days: propertyChartResponse.data.filters.days,
      device_type: propertyChartResponse.data.filters.device_type as DeviceType,
    };

    return currentValues
  }, [propertyChartResponse.data?.filters]);

  // Merge server and user filters, prioritizing user selections while keeping server values
  // for properties that haven't been explicitly set by the user
  const actualFilters = useMemo(() => {
    return {
      ...defaultValues,
      ...serverFilters,
      ...filters
    };
  }, [filters, serverFilters]);


  // Update filter function
  const updateFilter = (key: keyof RealtimeFilterValues, value: any) => {
    setFilters(prev => ({ ...actualFilters, ...prev, [key]: value }));
  };

  const filterOptions = DEFAULT_RUNTIME_FILTER_OPTIONS;

  return {
    propertyChartResponse,
    filters: actualFilters,
    filterOptions,
    updateFilter,
    isParametersAdjusted: filters === undefined && serverFilters,
  };
};
